import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from 'react';
import NumberFormat from 'react-number-format';
import useSWR from 'swr';
import { APIBatchOperation } from '../../../../../../typings/BatchOperation.interface';
import APIMethodKeys from '../../../client/APIMethodKeys';
import LINKS from '../../../configs/links';
import { GlobalContext } from '../../contexts/GlobalContext';
import { formatEventFiltersForAPI } from '../../hooks/useEventList';
import { useIsQuotaEnforcementActive } from '../../hooks/useIsQuotaEnforcementActive';
import { formatRequestFiltersForAPI } from '../../hooks/useRequestList';
import Alert from '../base/Alert';
import Button from '../base/Button';
import { StyledCardSection } from '../base/Card';
import Icon from '../base/Icon';
import Skeleton from '../base/Skeleton';
import Text from '../base/Text';
import Tooltip from '../base/Tooltip';
import { useDialog } from '../Dialog';
import { Div } from '../helpers/StyledUtils';
import { useToasts } from '../Toast';
import BulkOperationBreakdown from './BulkOperationBreakdown';
import { BulkOperationFilters, BulkOperationModel, BulkOperationType } from './types';
import { BULK_OPERATION_CONFIGS, cleanupQueryForBulkOperation, ensureDateFilters, getOperationLabels, hasInvalidStatuses, hasNoFilters, hasOngoingIdenticalOperation } from './utils';
interface SubmitBulkOperationProps {
  operation_type: BulkOperationType;
  model?: BulkOperationModel;
  query: BulkOperationFilters;
  revalidateCounts: () => void;
  onSubmit?: () => void;
}
const SubmitBulkOperation: React.FC<SubmitBulkOperationProps> = t0 => {
  const $ = _c(89);
  const {
    operation_type,
    model,
    query,
    onSubmit,
    revalidateCounts
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [submitted_operation, setSubmittedOperation] = useState(null);
  const [pending, setPending] = useState(false);
  const {
    addToast
  } = useToasts();
  const is_quota_enforcement_active = useIsQuotaEnforcementActive();
  const config = BULK_OPERATION_CONFIGS[operation_type];
  let t1;
  if ($[0] !== model || $[1] !== operation_type) {
    t1 = getOperationLabels(operation_type, model);
    $[0] = model;
    $[1] = operation_type;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const labels = t1;
  const cleaned_query = cleanupQueryForBulkOperation(query);
  const query_with_date = ensureDateFilters(cleaned_query);
  const showDialog = useDialog();
  let t2;
  bb0: {
    if (operation_type === "cancel") {
      t2 = formatEventFiltersForAPI(query_with_date as any);
      break bb0;
    }
    const formatters = {
      events: formatEventFiltersForAPI,
      requests: _temp,
      ignored_events: _temp2
    };
    t2 = formatters[model || "events"](query_with_date as any);
  }
  const api_query = t2;
  const has_invalid_status = hasInvalidStatuses(query_with_date);
  let t3;
  if ($[3] !== config.list_api_key || $[4] !== model || $[5] !== operation_type) {
    t3 = operation_type === "cancel" ? APIMethodKeys.bulk.cancelEvents.list({
      in_progress: true,
      dir: "desc"
    }) : APIMethodKeys[config.list_api_key][model || "events"].list({
      in_progress: true,
      dir: "desc"
    });
    $[3] = config.list_api_key;
    $[4] = model;
    $[5] = operation_type;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== HookdeckAPI || $[8] !== config.list_api_key || $[9] !== model || $[10] !== operation_type) {
    t4 = () => {
      if (operation_type === "cancel") {
        return HookdeckAPI.bulk.cancelEvents.list({
          in_progress: true,
          dir: "desc"
        });
      } else {
        return HookdeckAPI[config.list_api_key][model || "events"].list({
          in_progress: true,
          dir: "desc"
        });
      }
    };
    $[7] = HookdeckAPI;
    $[8] = config.list_api_key;
    $[9] = model;
    $[10] = operation_type;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      refreshInterval: 1000
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data: ongoing_operations
  } = useSWR(t3, t4, t5);
  const ongoing_identical_operation = operation_type === "cancel" && ongoing_operations ? hasOngoingIdenticalOperation(ongoing_operations.models || [], api_query) : false;
  let t6;
  if ($[13] !== config.plan_api_key || $[14] !== model || $[15] !== operation_type) {
    t6 = () => {
      if (operation_type === "cancel") {
        return APIMethodKeys.bulk.cancelEvents;
      }
      return APIMethodKeys[config.plan_api_key][model || "events"];
    };
    $[13] = config.plan_api_key;
    $[14] = model;
    $[15] = operation_type;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const getPlanApiMethodKey = t6;
  const {
    data: plan,
    isLoading: is_plan_loading
  } = useSWR(getPlanApiMethodKey().plan({
    query: api_query
  }), () => {
    if (operation_type === "cancel") {
      return HookdeckAPI.bulk.cancelEvents.plan({
        query: api_query
      });
    } else {
      return HookdeckAPI[config.plan_api_key][model || "events"].plan({
        query: api_query
      });
    }
  });
  let t7;
  if ($[17] !== config.list_api_key || $[18] !== model || $[19] !== operation_type) {
    t7 = () => {
      if (operation_type === "cancel") {
        return APIMethodKeys.bulk.cancelEvents;
      }
      return APIMethodKeys[config.list_api_key][model || "events"];
    };
    $[17] = config.list_api_key;
    $[18] = model;
    $[19] = operation_type;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const getListApiMethodKey = t7;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      refreshInterval: 5000
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    data: current_operations
  } = useSWR(getListApiMethodKey().list(submitted_operation ? {
    id: submitted_operation.id
  } : {
    query: api_query,
    in_progress: true
  }), () => {
    if (operation_type === "cancel") {
      return HookdeckAPI.bulk.cancelEvents.list(submitted_operation ? {
        id: submitted_operation.id
      } : {
        query: api_query,
        in_progress: true
      });
    } else {
      return HookdeckAPI[config.list_api_key][model || "events"].list(submitted_operation ? {
        id: submitted_operation.id
      } : {
        query: api_query,
        in_progress: true
      });
    }
  }, t8);
  const handleSubmit = () => {
    showDialog(() => {
      setPending(true);
      const api_call = operation_type === "cancel" ? HookdeckAPI.bulk.cancelEvents.create({
        query: api_query
      }) : HookdeckAPI.bulk[model || "events"].retry({
        query: api_query
      });
      api_call.then(operation => {
        setSubmittedOperation(operation);
        addToast("success", labels.success_message);
        setPending(false);
        revalidateCounts();
        if (onSubmit) {
          onSubmit();
        }
      }).catch(() => {
        addToast("error", labels.error_message);
        setPending(false);
      });
    }, _temp3, {
      title: labels.dialog_title,
      message: <Div flex={{
        direction: "column",
        gap: 4
      }}><Text>{labels.dialog_message}</Text><BulkOperationBreakdown operation_type={operation_type} model={model} query={api_query} /></Div>,
      footer_content: <Div h={100} flex={{
        align: "center",
        justify: "center"
      }}>{plan ? <Text bold={true}><NumberFormat renderText={_temp4} displayType="text" value={plan?.estimated_count || 0} thousandSeparator="," />{" "}{plan?.estimated_count === 1 ? labels.model_label.slice(0, -1) : labels.model_label}</Text> : <Skeleton h={{
          px: 20
        }} w={{
          px: 200
        }} loading={true} />}</Div>,
      cancel_label: labels.dialog_cancel_label,
      submit_label: labels.dialog_submit_label,
      danger: config.button_style === "danger"
    });
  };
  const has_pending_status = operation_type === "retry" && model === "events" && (query_with_date as any).status?.includes("pending");
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      b: 4
    };
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = operation_type === "retry" ? `Retry all ${labels.model_label} matching the conditions below. ${model === "events" ? "Events with pending retries are excluded from the bulk retry count." : ""}` : "Pending, Paused, and Delayed events, and/or Successful or Failed events with scheduled attempts, can be cancelled before reaching their destination.";
  const t11 = `${model === "events" ? "Event" : "Request"} docs`;
  const t12 = model === "events" ? LINKS.product_docs.events : LINKS.product_docs.requests;
  let t13;
  if ($[23] !== t11 || $[24] !== t12) {
    t13 = {
      label: t11,
      to: t12
    };
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      cursor: "default"
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== labels.model_label) {
    t15 = <Text as="span" underline={true} style={t14}>eligible {labels.model_label}</Text>;
    $[27] = labels.model_label;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t10 || $[30] !== t13 || $[31] !== t15) {
    t16 = <Tooltip as="span" side="bottom" tooltip={t10} cta={t13}>{t15}</Tooltip>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== operation_type || $[34] !== t16) {
    t17 = <Text m={t9}>Bulk {operation_type} all{" "}{t16}{" "}where:</Text>;
    $[33] = operation_type;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== api_query || $[37] !== model || $[38] !== operation_type) {
    t18 = <BulkOperationBreakdown operation_type={operation_type} model={model} query={api_query} />;
    $[36] = api_query;
    $[37] = model;
    $[38] = operation_type;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== has_invalid_status || $[41] !== operation_type) {
    t19 = has_invalid_status && operation_type === "cancel" && <Alert warning={true} inline={true} m={{
      t: 4
    }}>Only events that are either queued, paused, delayed, or have future scheduled attempts can be cancelled. Events not matching that criteria are excluded from the bulk cancel operation.</Alert>;
    $[40] = has_invalid_status;
    $[41] = operation_type;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== has_pending_status) {
    t20 = has_pending_status && <Alert warning={true} inline={true} m={{
      t: 4
    }}>Events with pending retries are excluded from the bulk retry count.</Alert>;
    $[43] = has_pending_status;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t17 || $[46] !== t18 || $[47] !== t19 || $[48] !== t20) {
    t21 = <StyledCardSection p={4}>{t17}{t18}{t19}{t20}</StyledCardSection>;
    $[45] = t17;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      x: 4,
      y: 3
    };
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      justify: "space-between",
      align: "center"
    };
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== api_query || $[53] !== is_plan_loading || $[54] !== labels.model_label || $[55] !== model || $[56] !== operation_type || $[57] !== plan?.estimated_count) {
    t24 = !hasNoFilters(api_query) ? <Text bold={true} flex={{
      align: "center",
      gap: 1
    }} shimmering={is_plan_loading}>{is_plan_loading ? "Loading" : <NumberFormat renderText={_temp5} displayType="text" value={plan?.estimated_count ?? 0} thousandSeparator="," />}{" "}{labels.model_label}<Tooltip tooltip={`This count represents the number of ${labels.model_label} eligible for bulk ${operation_type}, given the conditions applied above.${model === "events" && operation_type === "retry" ? " If this value doesn't match the total count on your event list, Hookdeck has ignored events ineligible for retry." : " If this value doesn't match the total count on your event list, some events are ineligible for cancellation."}`} cta={{
        label: `${model === "events" || !model ? "Event" : "Request"} docs`,
        to: model === "events" || !model ? LINKS.product_docs.events : LINKS.product_docs.requests
      }}><Icon icon="info" p={0.5} /></Tooltip></Text> : <span />;
    $[52] = api_query;
    $[53] = is_plan_loading;
    $[54] = labels.model_label;
    $[55] = model;
    $[56] = operation_type;
    $[57] = plan?.estimated_count;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  const t25 = is_quota_enforcement_active && operation_type === "retry" ? "Retry disabled - Plan limit exceeded. Upgrade to enable." : ongoing_identical_operation ? "An identical bulk operation is already ongoing." : undefined;
  const t26 = !ongoing_identical_operation && !(is_quota_enforcement_active && operation_type === "retry");
  const t27 = config.button_style === "danger";
  let t28;
  if ($[59] !== api_query || $[60] !== is_quota_enforcement_active || $[61] !== ongoing_identical_operation || $[62] !== operation_type || $[63] !== pending || $[64] !== plan?.estimated_count) {
    t28 = plan?.estimated_count === 0 || pending || hasNoFilters(api_query) || ongoing_identical_operation || is_quota_enforcement_active && operation_type === "retry";
    $[59] = api_query;
    $[60] = is_quota_enforcement_active;
    $[61] = ongoing_identical_operation;
    $[62] = operation_type;
    $[63] = pending;
    $[64] = plan?.estimated_count;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== labels.action_label || $[67] !== pending) {
    t29 = pending ? <Icon icon="loading" /> : labels.action_label;
    $[66] = labels.action_label;
    $[67] = pending;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== handleSubmit || $[70] !== t27 || $[71] !== t28 || $[72] !== t29) {
    t30 = <Button.Permission role="member" onClick={handleSubmit} outline={true} danger={t27} disabled={t28}>{t29}</Button.Permission>;
    $[69] = handleSubmit;
    $[70] = t27;
    $[71] = t28;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== t25 || $[75] !== t26 || $[76] !== t30) {
    t31 = <Tooltip tooltip={t25} disabled={t26}>{t30}</Tooltip>;
    $[74] = t25;
    $[75] = t26;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t24 || $[79] !== t31) {
    t32 = <StyledCardSection p={t22}><Div flex={t23}>{t24}{t31}</Div></StyledCardSection>;
    $[78] = t24;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== current_operations?.models || $[82] !== operation_type || $[83] !== submitted_operation) {
    t33 = submitted_operation && current_operations?.models[0] && <StyledCardSection p={4}><Alert success={true} inline={true}>Bulk {operation_type} operation #{current_operations?.models[0].number} has been submitted.</Alert></StyledCardSection>;
    $[81] = current_operations?.models;
    $[82] = operation_type;
    $[83] = submitted_operation;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== t21 || $[86] !== t32 || $[87] !== t33) {
    t34 = <>{t21}{t32}{t33}</>;
    $[85] = t21;
    $[86] = t32;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  return t34;
};
export default SubmitBulkOperation;
function _temp(filters) {
  return formatRequestFiltersForAPI(filters, "created_at");
}
function _temp2(filters_0) {
  return filters_0;
}
function _temp3() {
  return null;
}
function _temp4(v) {
  return v;
}
function _temp5(v_0) {
  return v_0;
}